import {
  Box,
  Button,
  Flex,
  FormControl,
  Input,
  Select,
  Text,
  IconButton,
  HStack,
} from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { DoctorOverview } from "@/types";

const DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"] as const;

type Day = (typeof DAYS)[number];

interface TimeSlot {
  session_type: string;
  session_day: string;
  start_time: string;
  end_time: string;
}

interface AppointmentScheduleProps {
  register: any;
  watch: any;
  setValue: any;
  editingUser?: DoctorOverview | null;
}

const AppointmentSchedule: React.FC<AppointmentScheduleProps> = ({ register, watch, setValue, editingUser = null }) => {
  const [selectedDay, setSelectedDay] = useState<Day>("monday");
  const [slots, setSlots] = useState<{ [key in Day]: number }>({
    monday: 1,
    tuesday: 1,
    wednesday: 1,
    thursday: 1,
    friday: 1,
    saturday: 1,
    sunday: 1,
  });

  useEffect(() => {
    if (editingUser && editingUser.availabilities && editingUser.availabilities.length > 0) {
      const formattedAvailabilities = editingUser.availabilities.map(avail => ({
        session_type: avail.session_type,
        session_day: avail.session_day,
        start_time: avail.start_time,
        end_time: avail.end_time,
      }));
      
      setValue('availability', formattedAvailabilities);

      const slotCounts: { [key in Day]: number } = {
        monday: 0,
        tuesday: 0,
        wednesday: 0,
        thursday: 0,
        friday: 0,
        saturday: 0,
        sunday: 0,
      };

      editingUser.availabilities.forEach(avail => {
        const day = avail.session_day.toLowerCase() as Day;
        if (DAYS.includes(day)) {
          slotCounts[day]++;
        }
      });

      Object.keys(slotCounts).forEach(day => {
        if (slotCounts[day as Day] === 0) {
          slotCounts[day as Day] = 1;
        }
      });

      setSlots(slotCounts);
    }
  }, [editingUser, setValue]);

  const getCurrentAvailability = (): TimeSlot[] => {
    return watch("availability") || [];
  };

  const addSlot = () => {
    const currentAvailability = getCurrentAvailability();
    const newSlot: TimeSlot = {
      session_type: "",
      session_day: selectedDay,
      start_time: "",
      end_time: "",
    };
    setValue("availability", [...currentAvailability, newSlot]);
    setSlots((prev) => ({
      ...prev,
      [selectedDay]: prev[selectedDay] + 1,
    }));
  };

  const removeSlot = (globalIndex: number) => {
    const currentAvailability = getCurrentAvailability();
    const updatedAvailability = currentAvailability.filter((_, idx) => idx !== globalIndex);
    setValue("availability", updatedAvailability);
    
    setSlots((prev) => ({
      ...prev,
      [selectedDay]: Math.max(1, prev[selectedDay] - 1),
    }));
  };

  const applyToAll = () => {
    const currentAvailability = getCurrentAvailability();
    const currentDaySlots = currentAvailability.filter(slot => slot.session_day === selectedDay);
    
    const newAvailability: TimeSlot[] = [];
    DAYS.forEach((day) => {
      currentDaySlots.forEach(slot => {
        newAvailability.push({
          ...slot,
          session_day: day,
        });
      });
    });
    
    setValue("availability", newAvailability);
    
    const newSlots = { ...slots };
    DAYS.forEach((day) => {
      newSlots[day] = currentDaySlots.length || 1;
    });
    setSlots(newSlots);
  };

  const currentDaySlots = getCurrentAvailability()
    .map((slot, index) => ({ slot, index }))
    .filter(({ slot }) => slot.session_day === selectedDay);

  return (
    <Box>
      <HStack spacing={2} mb={4} flexWrap="wrap">
        {DAYS.map((day) => (
          <Button
            key={day}
            size="md"
            colorScheme={selectedDay === day ? "red" : "gray"}
            variant={selectedDay === day ? "solid" : "outline"}
            onClick={() => setSelectedDay(day)}
            textTransform="capitalize"
          >
            {day}
          </Button>
        ))}
      </HStack>

      <Box>
        <Flex gap={4} mb={2} align="center">
          <Text fontWeight="medium" flex="1">Session</Text>
          <Text fontWeight="medium" flex="1">From</Text>
          <Text fontWeight="medium" flex="1">To</Text>
          <Box w="40px" />
        </Flex>

        {currentDaySlots.map(({ slot, index: globalIndex }) => (
          <Flex key={globalIndex} gap={2} mb={2} align="center">
            <FormControl flex="1">
              <Select placeholder="Select" {...register(`availability.${globalIndex}.session_type`)}>
                <option value="morning">Morning</option>
                <option value="afternoon">Afternoon</option>
                <option value="evening">Evening</option>
              </Select>
              <input type="hidden" {...register(`availability.${globalIndex}.session_day`)} value={selectedDay} />
            </FormControl>

            <FormControl flex="1">
              <Input type="time" {...register(`availability.${globalIndex}.start_time`)} />
            </FormControl>

            <FormControl flex="1">
              <Input type="time" {...register(`availability.${globalIndex}.end_time`)} />
            </FormControl>

            <IconButton
              aria-label="Add slot"
              icon={<Plus size={18} />}
              size="sm"
              colorScheme="gray"
              variant="ghost"
              onClick={addSlot}
            />

            {currentDaySlots.length > 1 && (
              <IconButton
                aria-label="Remove slot"
                icon={<Trash2 size={18} />}
                size="sm"
                colorScheme="red"
                variant="ghost"
                onClick={() => removeSlot(globalIndex)}
              />
            )}
          </Flex>
        ))}

        {currentDaySlots.length === 0 && (
          <Flex gap={2} mb={2} align="center">
            <FormControl flex="1">
              <Select placeholder="Select" disabled />
            </FormControl>
            <FormControl flex="1">
              <Input type="time" disabled />
            </FormControl>
            <FormControl flex="1">
              <Input type="time" disabled />
            </FormControl>
            <IconButton
              aria-label="Add slot"
              icon={<Plus size={18} />}
              size="sm"
              colorScheme="gray"
              variant="ghost"
              onClick={addSlot}
            />
          </Flex>
        )}

        <Button
          mt={4}
          bg="black"
          color="white"
          size="sm"
          onClick={applyToAll}
          _hover={{ bg: "gray.800" }}
        >
          Apply All
        </Button>
      </Box>
    </Box>
  );
};

export default AppointmentSchedule;